// Pure milestone catalog + threshold checks.
//
// The six audit-F8 milestone keys + their non-judgmental copy. Kept in a
// pure module (no React, no fetch) so the threshold logic can be unit-
// tested in isolation. The server reuses the same key list and threshold
// numbers when deciding whether to INSERT OR IGNORE on read, so there
// is no drift between "what the server unlocked" and "what the client
// would have shown".
//
// Copy guidelines: under 110 chars, present-tense, no shame language,
// no comparison-to-others framing. Each unlock should feel like a
// quiet observation, not a trophy.

export type MilestoneKey =
  | 'streak-7'
  | 'streak-30'
  | 'streak-45'
  | 'streak-90'
  | 'streak-180'
  | 'reduction-25'
  | 'reduction-50'
  | 'dose-free'
  | 'dose-free-30'
  | 'dose-free-90'
  | 'dose-free-180';

export interface MilestoneDef {
  key: MilestoneKey;
  /** Group used for the "next milestone" hint on the Today card. */
  group: 'streak' | 'reduction' | 'dose-free';
  /** Threshold the user must hit. Units: days for streak/dose-free, percent for reduction. */
  threshold: number;
  /** Display order — higher = celebrated above lower ones when multiple unlock at once. */
  rank: number;
  title: string;
  body: string;
  /** Short label for the "next milestone" progress hint, e.g. "30-day streak". */
  nextLabel: string;
}

export const MILESTONES: ReadonlyArray<MilestoneDef> = [
  {
    key: 'streak-7',
    group: 'streak',
    threshold: 7,
    rank: 70,
    title: 'Seven days in a row.',
    body: '7 days in a row of checking in — a quiet kind of consistency.',
    nextLabel: '7-day streak',
  },
  {
    key: 'streak-30',
    group: 'streak',
    threshold: 30,
    rank: 300,
    title: 'Thirty days in a row.',
    body: 'A full month of showing up. The pattern is yours now.',
    nextLabel: '30-day streak',
  },
  {
    key: 'streak-45',
    group: 'streak',
    threshold: 45,
    rank: 450,
    title: 'Forty-five days in a row.',
    body: 'You\'re past the worst PAWS week. The body is rebuilding underneath the surface.',
    nextLabel: '45-day streak',
  },
  {
    key: 'streak-90',
    group: 'streak',
    threshold: 90,
    rank: 900,
    title: 'Ninety days in a row.',
    body: 'Three months of consistency. Habit territory — not luck.',
    nextLabel: '90-day streak',
  },
  {
    key: 'streak-180',
    group: 'streak',
    threshold: 180,
    rank: 1800,
    title: 'Six months in a row.',
    body: 'Half a year of showing up. Past 180 days the relapse rate drops sharply — you are inside the safe stretch.',
    nextLabel: '6-month streak',
  },
  {
    key: 'reduction-25',
    group: 'reduction',
    threshold: 25,
    rank: 250,
    title: 'A quarter off baseline.',
    body: "Today's target is 25% below where you started. The first real step.",
    nextLabel: '25% reduction',
  },
  {
    key: 'reduction-50',
    group: 'reduction',
    threshold: 50,
    rank: 500,
    title: 'Half-off baseline.',
    body: "Today's target is half of where you started. Halfway is where most people quit. You didn't.",
    nextLabel: '50% reduction',
  },
  {
    key: 'dose-free',
    group: 'dose-free',
    threshold: 1,
    rank: 1000,
    title: 'First dose-free day.',
    body: "A whole day without. Hold onto this — it's the proof you have it in you.",
    nextLabel: 'first dose-free day',
  },
  {
    key: 'dose-free-30',
    group: 'dose-free',
    threshold: 30,
    rank: 3000,
    title: 'Thirty dose-free days.',
    body: 'A month with none. The body is past the chemistry; what remains is the day-to-day choice.',
    nextLabel: '30 dose-free days',
  },
  {
    key: 'dose-free-90',
    group: 'dose-free',
    threshold: 90,
    rank: 9000,
    title: 'Ninety dose-free days.',
    body: 'Three months without. The window where most people relapse is behind you.',
    nextLabel: '90 dose-free days',
  },
  {
    key: 'dose-free-180',
    group: 'dose-free',
    threshold: 180,
    rank: 18000,
    title: 'Six months dose-free.',
    body: 'Half a year. The "new normal" is the normal now.',
    nextLabel: '180 dose-free days',
  },
];

export interface MilestoneProgress {
  /** Current consecutive-day streak. */
  streakDays: number;
  /** % the user's current target is below baseline (0 if no plan / no baseline). */
  reductionPct: number;
  /**
   * Whole days since the user's most recent logged dose. Null if they have
   * either never logged a dose OR logged one today (i.e. today is not
   * dose-free).
   */
  daysSinceLastDose: number | null;
}

/**
 * Given current progress, return every milestone key the user currently
 * meets. The caller (server handler) INSERTs OR IGNOREs these into the
 * user_milestones table so re-meeting after a break doesn't re-unlock.
 */
export function meetsThresholds(progress: MilestoneProgress): MilestoneKey[] {
  const unlocked: MilestoneKey[] = [];
  for (const m of MILESTONES) {
    if (currentlyMeets(m, progress)) unlocked.push(m.key);
  }
  return unlocked;
}

export function currentlyMeets(
  def: MilestoneDef,
  progress: MilestoneProgress,
): boolean {
  if (def.group === 'streak') {
    return progress.streakDays >= def.threshold;
  }
  if (def.group === 'reduction') {
    return progress.reductionPct >= def.threshold;
  }
  // dose-free: any logged dose-free day after the user has logged at
  // least one dose previously. daysSinceLastDose === null means either
  // "never logged" (not a milestone moment) or "logged today" (not yet
  // dose-free). Either way, no unlock.
  return progress.daysSinceLastDose != null && progress.daysSinceLastDose >= def.threshold;
}

/**
 * Pick the milestone def most recently in scope to surface. Strategy:
 * highest-rank unlocked milestone the user meets right now (so a 90-day
 * streak does not get out-shouted by an old 7-day unlock). Returns null
 * if the unlocked list is empty.
 */
export function pickHighlight(
  unlockedKeys: ReadonlyArray<string>,
): MilestoneDef | null {
  const set = new Set(unlockedKeys);
  const candidates = MILESTONES.filter((m) => set.has(m.key));
  if (candidates.length === 0) return null;
  return candidates.reduce((best, m) => (m.rank > best.rank ? m : best));
}

/**
 * The next-up milestone within the same group as the user's current
 * focus area. Strategy: take the highest-ranked currently-unlocked
 * milestone's group (or default to 'streak' if nothing unlocked) and
 * find the smallest-threshold not-yet-met milestone in that group.
 * Returns null when the user has unlocked everything available.
 */
export function pickNext(
  progress: MilestoneProgress,
  unlockedKeys: ReadonlyArray<string>,
): { def: MilestoneDef; current: number } | null {
  const set = new Set(unlockedKeys);
  const highlight = pickHighlight(unlockedKeys);
  // Choose a focus group:
  //   - if dose-free milestone is in flight (user has any daysSinceLastDose),
  //     prefer that group
  //   - else continue from whatever the user just unlocked
  //   - else default to streak (the most universally relevant first step)
  let group: MilestoneDef['group'] = highlight?.group ?? 'streak';
  if (progress.daysSinceLastDose != null && !set.has('dose-free')) {
    group = 'dose-free';
  }
  const inGroup = MILESTONES.filter((m) => m.group === group && !set.has(m.key));
  if (inGroup.length === 0) {
    // Group complete — try any other group with remaining unlocks.
    const other = MILESTONES.filter((m) => !set.has(m.key));
    if (other.length === 0) return null;
    const pick = other.reduce((min, m) => (m.threshold < min.threshold ? m : min));
    return { def: pick, current: progressValueFor(pick.group, progress) };
  }
  const pick = inGroup.reduce((min, m) => (m.threshold < min.threshold ? m : min));
  return { def: pick, current: progressValueFor(pick.group, progress) };
}

function progressValueFor(
  group: MilestoneDef['group'],
  progress: MilestoneProgress,
): number {
  if (group === 'streak') return progress.streakDays;
  if (group === 'reduction') return Math.round(progress.reductionPct);
  return progress.daysSinceLastDose ?? 0;
}
